/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import styled from '@emotion/styled/macro';
import DateRangeIcon from '@mui/icons-material/DateRange';
import {
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DBCurrency } from '@prisma/client';
import api from '@tyrio/api-factory';
import { DBBranchesApi } from '@tyrio/dto';
import moment from 'moment';
import { useContext, useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { parseEnumToArray } from '../../../../../helpers/enum-parser';
import {
  InboundInvoiceContext,
  VAT,
} from '../../../context/InboundInvoiceContext';
import { getAutocompleteSingleField, getTextField } from '../../form-helper';
import { CustomSummary } from '../components/CustomSummary';

interface MainProps {
  errors: FieldErrors;
  control: Control;
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<any>;
  onSubmit: (data: any) => void;
  handleSubmit: UseFormHandleSubmit<any>;
  watch: UseFormWatch<any>;
}

const CURRENCY_TYPES = parseEnumToArray(DBCurrency);
const VAT_CHARGE_TYPES = parseEnumToArray(VAT);

export const Main = ({
  errors,
  control,
  register,
  setValue,
  onSubmit,
  handleSubmit,
  watch,
}: MainProps) => {
  const { t } = useTranslation();
  const {
    branches,
    setBranches,
    shouldUpdateWorkOrder,
    setShouldUpdateWorkOrder,
    setCreateTransferOrder,
    createTransferOrder,
  } = useContext(InboundInvoiceContext);

  const [openDatePicker, setOpenDatePicker] = useState<Record<number, boolean>>(
    { 0: false, 1: false, 2: false }
  );

  useQuery(
    ['get_branches'],
    async () => {
      return await api.fetch<DBBranchesApi['list']>('get_branches', {
        search: '',
      });
    },
    {
      onSuccess: (data) => {
        const dropdownItems = [{ value: 'all', label: 'All' }];
        data.data.forEach((item) =>
          dropdownItems.push({
            label: item.branchName,
            value: item.id.toString(),
          })
        );

        setBranches(dropdownItems);
      },
    }
  );

  const handleOpeningDatePicker = (index: number) => {
    const isOpen = openDatePicker[index];
    if (!isOpen)
      Object.keys(openDatePicker).forEach((item) => {
        setOpenDatePicker((prevState) => ({
          ...prevState,
          [item]: false,
        }));
      });
    setOpenDatePicker((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const newDate = new Date();

  const getDelay = () => {
    const invoiceIssueDate = watch('invoiceIssueDate');
    const invoiceDueDate = watch('invoiceDueDate');

    const invoiceIssueMoment = moment(invoiceIssueDate)
      .hours(newDate.getHours())
      .minute(newDate.getMinutes())
      .second(newDate.getSeconds());

    const invoiceDueMoment = moment(invoiceDueDate)
      .hours(newDate.getHours())
      .minute(newDate.getMinutes())
      .second(newDate.getSeconds());

    let dateDelay = invoiceDueMoment.diff(invoiceIssueMoment, 'days');

    if (dateDelay < 0) dateDelay = 0;

    setValue('delay', dateDelay);
  };

  const getInvoiceDueDate = (v: string) => {
    const invoiceIssueDate = watch('invoiceIssueDate');

    const date = moment(invoiceIssueDate)
      .hours(newDate.getHours())
      .minute(newDate.getMinutes())
      .second(newDate.getSeconds());

    date.add(v, 'days');

    setValue('invoiceDueDate', date.toDate());
    setValue('delay', v);
  };

  const getInvoiceIssueDate = () => {
    const invoiceIssueDate = watch('invoiceIssueDate');
    const delay = watch('delay');

    const date = moment(invoiceIssueDate)
      .hours(newDate.getHours())
      .minute(newDate.getMinutes())
      .second(newDate.getSeconds());

    // set issue date
    setValue('invoiceIssueDate', date.toDate());
    // add delay
    date.add(delay, 'days');
    // set delay date
    setValue('invoiceDueDate', date.toDate());
  };

  const issueDate = watch('invoiceIssueDate');
  const dueDate = watch('invoiceDueDate');

  const issueDateMoment = moment(issueDate)
    .hours(newDate.getHours())
    .minute(newDate.getMinutes())
    .second(newDate.getSeconds());

  const dueDateMoment = moment(dueDate)
    .hours(newDate.getHours())
    .minute(newDate.getMinutes())
    .second(newDate.getSeconds());

  const dateDelay = dueDateMoment.diff(issueDateMoment, 'days');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Wrapper>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Grid style={{ width: '100%' }}>
            {getAutocompleteSingleField(
              t('Branch'),
              'branchId',
              () => true,
              errors,
              control,
              { lg: 4, xs: 12 },
              {},
              branches,
              false,
              watch('branchId')
            )}
          </Grid>
          <Grid style={{ width: '100%' }}>
            <DatePicker
              open={openDatePicker[0]}
              label="datePicker"
              inputFormat="dd.MM.yyyy"
              value={watch('date')}
              onChange={(newValue) => {
                setValue('date', newValue);
                const date = moment(newValue)
                  .hours(newDate.getHours())
                  .minute(newDate.getMinutes())
                  .second(newDate.getSeconds());
                setValue('date', date.toDate());
                setOpenDatePicker({ ...openDatePicker, 0: false });
              }}
              renderInput={(params) => (
                <TextField
                  id="date"
                  {...params}
                  label="Date"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton onClick={() => handleOpeningDatePicker(0)}>
                          <DateRangeIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment: null,
                  }}
                  error={errors['date']}
                  helperText={errors['date'] ?? ''}
                />
              )}
            />
          </Grid>
        </div>
        <Grid item style={{ width: '100%' }}>
          {getTextField(
            t('Supplier name'),
            'supplierName',
            () => false,
            errors,
            register,
            { lg: 4, xs: 12 },
            '',
            { disabled: true }
          )}
        </Grid>

        <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
          <Grid item style={{ width: '100%' }}>
            {getAutocompleteSingleField(
              t('Vat charge type'),
              'vatChargeType',
              () => true,
              errors,
              control,
              { lg: 4, xs: 12 },
              {},
              VAT_CHARGE_TYPES
            )}
          </Grid>
          <Grid item style={{ width: '70%' }}>
            {getAutocompleteSingleField(
              t('Currency'),
              'currency',
              () => true,
              errors,
              control,
              { lg: 4, xs: 12 },
              {},
              CURRENCY_TYPES
            )}
          </Grid>
          <Grid item style={{ width: '50%' }}>
            {getTextField(
              t('Exchange rate'),
              'exchangeRate',
              () => false,
              errors,
              register,
              { lg: 3, xs: 12 },
              ''
            )}
          </Grid>
        </div>

        <Divider
          variant="fullWidth"
          sx={{
            borderWidth: '2px',
            borderColor: '#E6E8F0',
          }}
        />

        <Grid item style={{ width: '100%' }}>
          {getTextField(
            t('Supplier invoice number'),
            'supplierInvoiceNumber',
            () => true,
            errors,
            register,
            { lg: 3, xs: 12 }
          )}
        </Grid>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Grid style={{ width: '100%' }}>
            <DatePicker
              label="datePicker"
              inputFormat="dd.MM.yyyy"
              value={watch('invoiceIssueDate')}
              onChange={(newValue) => {
                setValue('invoiceIssueDate', newValue);
                getInvoiceIssueDate();
              }}
              renderInput={(params) => (
                <TextField
                  id="invoiceIssueDate"
                  {...params}
                  label="Invoice issue date"
                  helperText={
                    errors['invoiceIssueDate']
                      ? 'You have to choose issue date!'
                      : ''
                  }
                  error={errors['invoiceIssueDate']}
                />
              )}
            />
          </Grid>
          <Grid>
            {getTextField(
              t('Delay'),
              'delay',
              () => true,
              errors,
              register,
              { lg: 3, xs: 12 },
              '',
              undefined,
              undefined,
              undefined,
              (v) => {
                getInvoiceDueDate(v);
              }
            )}
          </Grid>
          <Grid style={{ width: '100%' }}>
            <DatePicker
              label="datePicker"
              inputFormat="dd.MM.yyyy"
              value={watch('invoiceDueDate')}
              onChange={(newValue) => {
                setValue('invoiceDueDate', newValue);
                getDelay();
              }}
              renderInput={(params) => (
                <TextField
                  id="invoiceDueDate"
                  {...params}
                  label="Invoice due date"
                  error={errors['invoiceDueDate'] || errors['invoiceIssueDate']}
                  helperText={
                    dateDelay < 0
                      ? 'Must be greater than issue date'
                      : errors['invoiceIssueDate']
                      ? 'You have to choose due date!'
                      : ''
                  }
                />
              )}
            />
          </Grid>
        </div>

        <CustomSummary />

        <FormControlLabel
          style={{ paddingLeft: '16px' }}
          control={
            <Switch
              data-cy="switch"
              color="info"
              onChange={() => {
                if (setShouldUpdateWorkOrder)
                  setShouldUpdateWorkOrder(!shouldUpdateWorkOrder);
              }}
              checked={shouldUpdateWorkOrder}
              disabled={false}
            />
          }
          label={'Update work orders'}
        />

        <FormControlLabel
          style={{ paddingLeft: '16px' }}
          control={
            <Switch
              data-cy="switch"
              color="info"
              onChange={() => {
                if (setCreateTransferOrder)
                  setCreateTransferOrder(!createTransferOrder);
              }}
              checked={createTransferOrder}
              disabled={false}
            />
          }
          label={'Create transfer order'}
        />

        <RemarkWrapper>
          {getTextField(
            t('Internal remark'),
            'internalRemark',
            () => false,
            errors,
            register,
            { lg: 3, xs: 12 },
            ''
          )}
        </RemarkWrapper>
      </Wrapper>
    </form>
  );
};

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 16px;
  height: calc(100svh - 230px);
  overflow-y: auto;
`;

const RemarkWrapper = styled.div`
  position: sticky;
  top: 100%;
`;
